import React from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useUserQuestionResponseCount} from '../../../../../hooks/useUserQuestionResponseCount';
import {usePresentationMode} from '../../../../../hooks/usePresentationMode';
import {roomUserCountSelector} from '../../../../../store/selectors/roomRunFile.selectors';
import {questionReportsSelector} from '../../../../../store/selectors/analytics.selectors';
import Modal from '../../../../../incase-domain/components/modals/directory/Modal';
import ReportProgressBar from '../../../../../incase-domain/components/report/progressBar/ReportProgressBar';
import ReportSingleValue from '../../../../../incase-domain/components/report/singleValue/ReportSingleValue';
import ReportAnswerLikertScale from '../../../../../incase-domain/components/report/answer/likertScale/ReportAnswerLikertScale';
import {ELEMENT} from '../../../../../incase-domain/elements/base/ElementTypes';
import {IElementQuestionnaire} from '../../../../../incase-domain/elements/base/questionElementBase';
import {isLikertScaleElement} from '../utils/reports';
import AnswerDistribution from './AnswerDistribution';
import Button from '../../../../../incase-domain/components/button/Button';
import ElementLoadingSkeleton from '../../../../../incase-domain/components/loadingSkeleton/ElementLoadingSkeleton';

interface IQuestionReportModal {
    open: boolean;
    setOpen: (open: boolean) => void;
    element: IElementQuestionnaire;
}

function QuestionReportModal({open, setOpen, element}: IQuestionReportModal): JSX.Element {
    const {t} = useTranslation();

    return (
        <Modal
            isOpen={open}
            onCancel={() => setOpen(false)}
            header={t('report.questionStatistics')}
            description={element.question}
            icon="solar:chart-bold"
            size="lg"
            closeOnClickOutside={false}
        >
            <QuestionReportContent element={element} />
        </Modal>
    );
}

interface IQuestionReportContent {
    element: IElementQuestionnaire;
}

function QuestionReportContent({element}: IQuestionReportContent): JSX.Element {
    const {type, id} = element;
    const {t} = useTranslation();

    const {isPresenter} = usePresentationMode();
    const numberOfUserAnswers = useUserQuestionResponseCount(id, true);
    const numberOfUsersInRoom = useSelector(roomUserCountSelector);
    const questionReports = useSelector(questionReportsSelector);

    const [showCorrect, setShowCorrect] = React.useState<boolean>(false);
    const [lastQuestionId, setLastQuestionId] = React.useState<string>(id);

    React.useEffect(() => {
        if (id !== lastQuestionId) {
            setLastQuestionId(id);
            setShowCorrect(false);
        }
    }, [id, lastQuestionId]);

    const displayReport = React.useMemo(() => {
        const report = questionReports.find((qr) => qr.elementId === id);
        if (report && report.elementId === id) {
            return report;
        }
        return undefined;
    }, [questionReports, id]);

    const answerCount = numberOfUserAnswers ?? displayReport?.userAnsweredCount ?? 0;

    const dontShowPointsFor =
        type !== ELEMENT.LIKERT_SCALE &&
        type !== ELEMENT.SEQUENCE_QUESTION &&
        type !== ELEMENT.MATCH_PAIR_QUESTION &&
        type !== ELEMENT.YES_NO_QUESTION &&
        !element.isDebatable;

    return (
        <div className="question-report-modal">
            <div className="question-report-modal__stats-container">
                <ReportSingleValue label={t('report.answerCount')} value={answerCount} />
                <ReportSingleValue label={t('report.activeParticipants')} value={numberOfUsersInRoom} />
                {dontShowPointsFor && (
                    <>
                        <ReportSingleValue label={t('report.averagePoints')} value={displayReport?.averagePoints || 0} />
                        <ReportSingleValue label={t('report.maximumPoints')} value={displayReport?.maximumPoints || 0} />
                    </>
                )}
            </div>
            {!displayReport ? (
                <div className="question-report-modal__skeleton">
                    <ElementLoadingSkeleton />
                </div>
            ) : (
                <>
                    {dontShowPointsFor && (
                        <div className="question-report-modal__progress-section">
                            <ReportProgressBar
                                label={t('report.averagePointsScored')}
                                value={displayReport.averagePoints || 0}
                                maxValue={displayReport.maximumPoints || 0}
                            />
                        </div>
                    )}
                    {isLikertScaleElement(element) && (
                        <div className="question-report-modal__likert-section">
                            <ReportAnswerLikertScale questionReport={displayReport} likertQuestions={element.questions} />
                        </div>
                    )}
                    <AnswerDistribution element={element} questionReport={displayReport} showCorrect={showCorrect} />
                </>
            )}

            {isPresenter && (
                <div className="question-report-modal__footer">
                    <Button variant="outline" size="lg" color="primary" type="button" onClick={() => setShowCorrect(!showCorrect)}>
                        <span>{t(`report.${showCorrect ? 'hideCorrect' : 'showCorrect'}`)}</span>
                    </Button>
                </div>
            )}
        </div>
    );
}

export default QuestionReportModal;
